import { useEffect, useRef, useState } from "react";
import { UploadCloud, Download, RotateCcw, Loader2, ShieldCheck, AlertTriangle, Save } from "lucide-react";
import { createJob, getJob, downloadUrl, errorMessage, currentLimits, saveToHistory } from "../lib/api";
import { usePro } from "../context/ProContext";
import { useFileEntries } from "../lib/filelist";
import { FileSortList } from "./FileSortList";
import { PageGrid } from "./PageGrid";
import { MergePageGrid } from "./MergePageGrid";
import { SignaturePad } from "./SignaturePad";

const VISUAL_MODES = { girar: "rotate", reordenar: "reorder", "remover-paginas": "remove" };

const PHASE_LABEL = {
    idle: "",
    uploading: "Enviando arquivos…",
    queued: "Na fila…",
    processing: "Processando…",
    done: "Concluído",
    failed: "Falhou",
    expired: "Expirado",
};

function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function ToolRunner({ tool }) {
    const LIMITS = currentLimits();
    const { isPro } = usePro();
    const fl = useFileEntries({ maxFiles: LIMITS.maxFiles, maxFileMB: LIMITS.maxFileMB, multi: tool.multi });
    const [options, setOptions] = useState(() =>
        Object.fromEntries(tool.options.filter((o) => o.default !== undefined).map((o) => [o.key, o.default]))
    );
    const [phase, setPhase] = useState("idle");
    const [progress, setProgress] = useState(0);
    const [job, setJob] = useState(null);
    const [token, setToken] = useState(null);
    const [error, setError] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [textMode, setTextMode] = useState(false);
    const [historySaved, setHistorySaved] = useState(false);
    const [historySaving, setHistorySaving] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [mergeOrder, setMergeOrder] = useState(null); // juntar: sequencia de paginas da pre-visualizacao
    const signatureRef = useRef(null);
    const inputRef = useRef(null);
    const pollRef = useRef(null);
    const idemKey = useRef(crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}`);

    useEffect(() => () => clearInterval(pollRef.current), []);

    const addFiles = (list) => {
        setError(null);
        fl.addFiles(list);
    };

    const reset = () => {
        clearInterval(pollRef.current);
        fl.reset();
        setPhase("idle");
        setProgress(0);
        setJob(null);
        setToken(null);
        setError(null);
        setTextMode(false);
        setHistorySaved(false);
        setHistorySaving(false);
        setHistoryError(null);
        setMergeOrder(null);
        idemKey.current = crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}`;
    };

    const saveHistory = async () => {
        if (historySaving || historySaved || !job || !token) return;
        setHistorySaving(true);
        setHistoryError(null);
        try {
            await saveToHistory(job.job_id, token);
            setHistorySaved(true);
        } catch (err) {
            setHistoryError(errorMessage(err));
        } finally {
            setHistorySaving(false);
        }
    };

    const buildOptions = () => {
        const out = {};
        for (const opt of tool.options) {
            let v = options[opt.key];
            if ((opt.type === "text" || opt.type === "password") && (!v || !v.trim())) {
                if (opt.emptyMeans) v = opt.emptyMeans;
                else if (opt.showIf && options[opt.showIf.key] !== opt.showIf.value) continue;
            }
            if (opt.showIf && options[opt.showIf.key] !== opt.showIf.value) continue;
            if (v !== undefined && v !== "") out[opt.key] = v;
        }
        // modo visual de girar: rotacoes por pagina prevalecem sobre angulo/paginas
        if (options.rotations) {
            out.rotations = options.rotations;
            delete out.degrees;
            delete out.pages;
        }
        // assinar: paginas personalizadas viram o campo "pages" da API
        if (tool.signature && out.pages === "personalizada") {
            out.pages = options.pages_custom || "ultima";
            delete out.pages_custom;
        }
        // juntar: sequencia de paginas da pre-visualizacao (tela == processamento)
        if (tool.slug === "juntar" && mergeOrder && mergeOrder.length) {
            const indexOf = new Map(fl.displayed.map((e, i) => [e.id, i]));
            const seq = mergeOrder
                .map((key) => {
                    const [fid, pg] = key.split(":");
                    const idx = indexOf.get(fid);
                    return idx === undefined ? null : `${idx}:${pg}`;
                })
                .filter(Boolean);
            if (seq.length === mergeOrder.length) out.page_order = seq.join(",");
        }
        return out;
    };

    const startPolling = (jobId, token) => {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const data = await getJob(jobId, token);
                setJob(data);
                if (data.status === "done" || data.status === "failed" || data.status === "expired") {
                    clearInterval(pollRef.current);
                    setPhase(data.status);
                    // estado terminal: a proxima tentativa cria um job NOVO (nunca reabre o job antigo)
                    idemKey.current = crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}`;
                } else {
                    setPhase(data.status);
                }
            } catch {
                clearInterval(pollRef.current);
                setPhase("failed");
                setError("Perdemos a conexão com o servidor. Tente novamente.");
                idemKey.current = crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}`;
            }
        }, 1200);
    };

    const process = async () => {
        if (fl.entries.length < (tool.minFiles || 1)) {
            setError(`Envie pelo menos ${tool.minFiles || 1} arquivo(s).`);
            return;
        }
        // a sequencia enviada e EXATAMENTE a que o usuario ve na tela
        const seq = fl.uploadSequence();
        if (tool.signature) {
            if (!signatureRef.current) {
                setError("Desenhe sua assinatura antes de processar.");
                return;
            }
            const blob = await signatureRef.current.getBlob();
            if (!blob) {
                setError("Desenhe sua assinatura antes de processar.");
                return;
            }
            seq.files.push(new File([blob], "assinatura.png", { type: "image/png" }));
            seq.ids.push("assinatura");
            seq.names.push("assinatura.png");
        }
        setError(null);
        setPhase("uploading");
        setProgress(0);
        try {
            const created = await createJob(tool.slug, seq.files, buildOptions(), (e) => {
                if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
            }, idemKey.current, { ids: seq.ids, order: seq.ids, names: seq.names });
            setJob(created);
            setToken(created.token);
            setPhase("queued");
            startPolling(created.job_id, created.token);
        } catch (err) {
            setPhase("failed");
            setError(errorMessage(err));
            idemKey.current = crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}`;
        }
    };

    const busy = ["uploading", "queued", "processing"].includes(phase);
    const visibleOptions = tool.options.filter((o) => !o.showIf || options[o.showIf.key] === o.showIf.value);
    const visualMode = VISUAL_MODES[tool.slug];
    const firstFile = fl.entries[0]?.file;
    const singlePdf =
        fl.entries.length === 1 &&
        firstFile &&
        (firstFile.type === "application/pdf" || firstFile.name.toLowerCase().endsWith(".pdf"));
    const showVisualGrid = Boolean(visualMode && singlePdf && !textMode && phase !== "done");

    return (
        <div className="border-2 border-black bg-white hard-shadow" data-testid="tool-runner">
            {/* Dropzone */}
            {phase !== "done" && (
                <div
                    role="button"
                    tabIndex={0}
                    aria-label={`Enviar arquivos para ${tool.name}`}
                    data-testid="dropzone"
                    onClick={() => !busy && inputRef.current?.click()}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !busy && inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!busy) addFiles(e.dataTransfer.files); }}
                    className={`m-4 border-2 border-dashed p-8 text-center flex flex-col items-center justify-center cursor-pointer transition-colors min-h-[180px] ${
                        dragOver ? "border-[#0284C7] bg-[#F0F9FF]" : "border-gray-400 hover:border-black"
                    } ${busy ? "opacity-60 cursor-wait" : ""}`}
                >
                    <UploadCloud className="w-10 h-10 text-black mb-3" aria-hidden="true" />
                    <span className="font-poster text-2xl text-black">
                        {dragOver ? "SOLTE AQUI" : "ARRASTE SEUS ARQUIVOS"}
                    </span>
                    <span className="font-mono-tag text-xs text-gray-500 mt-2">
                        {tool.acceptLabel} · até {LIMITS.maxFileMB} MB {tool.multi ? `· máx. ${LIMITS.maxFiles} arquivos` : "· 1 arquivo"}
                        {isPro && " · PRO"}
                    </span>
                    <span className="mt-4 px-6 py-2.5 bg-black text-white hover:bg-[#0284C7] font-mono-tag text-xs font-bold uppercase transition-colors">
                        Selecionar arquivos
                    </span>
                    <input
                        ref={inputRef}
                        type="file"
                        className="hidden"
                        accept={tool.accept}
                        multiple={tool.multi}
                        data-testid="file-input"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    />
                </div>
            )}

            {/* Lista de arquivos: ordenar A-Z/Z-A, renomear e arrastar */}
            {fl.entries.length > 0 && phase !== "done" && (
                <FileSortList fl={fl} disabled={busy} prefix="" />
            )}

            {/* Juntar: pre-visualizacao de TODAS as paginas com arrastar-soltar */}
            {tool.slug === "juntar" && fl.entries.length >= 2 && phase !== "done" && (
                <MergePageGrid fl={fl} onChange={setMergeOrder} disabled={busy} />
            )}

            {/* Quadro de assinatura */}
            {tool.signature && fl.entries.length > 0 && phase !== "done" && (
                <div className="mx-4 mb-4 border-2 border-black p-4 bg-[#FAFAF9]">
                    <SignaturePad onReady={(api) => (signatureRef.current = api)} />
                </div>
            )}

            {/* Grade visual de paginas (girar / reordenar / remover) */}
            {showVisualGrid && (
                <div className="mx-4 mb-4 border-2 border-black p-4 bg-[#FAFAF9]">
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-mono-tag text-xs font-bold uppercase tracking-wider text-black">
                            Prévia das páginas
                        </span>
                        <button
                            type="button"
                            onClick={() => setTextMode(true)}
                            data-testid="switch-text-mode"
                            className="font-mono-tag text-[11px] font-bold uppercase underline decoration-[#0284C7] underline-offset-2 hover:text-[#0284C7]"
                        >
                            Prefiro digitar
                        </button>
                    </div>
                    <PageGrid
                        file={firstFile}
                        mode={visualMode}
                        onChange={(partial) => setOptions((o) => ({ ...o, ...partial }))}
                    />
                </div>
            )}
            {visualMode && textMode && phase !== "done" && fl.entries.length > 0 && (
                <div className="mx-4 mb-2 text-right">
                    <button
                        type="button"
                        onClick={() => setTextMode(false)}
                        data-testid="switch-visual-mode"
                        className="font-mono-tag text-[11px] font-bold uppercase underline decoration-[#0284C7] underline-offset-2 hover:text-[#0284C7]"
                    >
                        Voltar à prévia visual
                    </button>
                </div>
            )}

            {/* Opções da ferramenta */}
            {visibleOptions.length > 0 && fl.entries.length > 0 && phase !== "done" && !showVisualGrid && (
                <div className="mx-4 mb-4 border-2 border-black p-4 bg-[#FAFAF9] space-y-4" data-testid="tool-options">
                    {visibleOptions.map((opt) => (
                        <div key={opt.key}>
                            <label htmlFor={`opt-${opt.key}`} className="block font-mono-tag text-xs font-bold uppercase tracking-wider text-black mb-2">
                                {opt.label}
                            </label>
                            {opt.type === "select" ? (
                                <select
                                    id={`opt-${opt.key}`}
                                    value={options[opt.key] ?? opt.default}
                                    disabled={busy}
                                    data-testid={`option-${opt.key}`}
                                    onChange={(e) => setOptions((o) => ({ ...o, [opt.key]: e.target.value }))}
                                    className="w-full border-2 border-black bg-white px-3 py-2.5 text-sm font-medium"
                                >
                                    {opt.choices.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    id={`opt-${opt.key}`}
                                    type={opt.type === "password" ? "password" : "text"}
                                    value={options[opt.key] ?? ""}
                                    disabled={busy}
                                    placeholder={opt.placeholder}
                                    data-testid={`option-${opt.key}`}
                                    onChange={(e) => setOptions((o) => ({ ...o, [opt.key]: e.target.value }))}
                                    className="w-full border-2 border-black bg-white px-3 py-2.5 text-sm font-medium"
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Ação principal */}
            {phase !== "done" && (
                <div className="px-4 pb-4">
                    <button
                        onClick={process}
                        disabled={busy || fl.entries.length === 0}
                        data-testid="process-button"
                        className="w-full py-4 bg-[#0284C7] hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-poster text-2xl tracking-wide transition-colors flex items-center justify-center gap-3"
                    >
                        {busy ? (
                            <>
                                <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" />
                                {PHASE_LABEL[phase]}
                            </>
                        ) : (
                            <>{tool.name.toUpperCase()} AGORA</>
                        )}
                    </button>
                    {phase === "uploading" && (
                        <div className="mt-3 border-2 border-black h-6 bg-white" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} data-testid="upload-progress">
                            <div className="h-full bg-[#22D3EE] transition-[width]" style={{ width: `${progress}%` }} />
                        </div>
                    )}
                    {(phase === "queued" || phase === "processing") && (
                        <p className="mt-3 font-mono-tag text-xs text-gray-600 text-center" data-testid="processing-status">
                            {PHASE_LABEL[phase]} Isso leva alguns segundos.
                        </p>
                    )}
                </div>
            )}

            {/* Erro */}
            {(error || fl.listError || phase === "failed") && (
                <div className="mx-4 mb-4 border-2 border-black bg-[#FEF2F2] p-4 flex items-start gap-3" role="alert" data-testid="error-box">
                    <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <div className="flex-1">
                        <p className="font-bold text-sm text-red-900">Não deu certo</p>
                        <p className="text-xs text-red-800 mt-1">{error || fl.listError || job?.error || "Erro interno ao processar. Tente novamente."}</p>
                        <button onClick={reset} data-testid="error-restart-button" className="mt-3 px-4 py-2 bg-black text-white font-mono-tag text-xs font-bold uppercase hover:bg-[#0284C7] transition-colors">
                            Tentar de novo
                        </button>
                    </div>
                </div>
            )}

            {/* Sucesso */}
            {phase === "done" && job && (
                <div className="m-4 border-2 border-black bg-[#F0F9FF] p-8 text-center" data-testid="success-box">
                    <ShieldCheck className="w-12 h-12 text-[#0284C7] mx-auto mb-3" aria-hidden="true" />
                    <p className="font-poster text-3xl text-black">PRONTO!</p>
                    <p className="font-mono-tag text-xs text-gray-600 mt-2">
                        {job.result_name} · {job.result_size ? formatBytes(job.result_size) : ""} · expira em até 60 min
                    </p>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                        <a
                            href={downloadUrl(job.job_id, token)}
                            download={job.result_name}
                            data-testid="download-button"
                            className="px-8 py-4 bg-[#0284C7] hover:bg-black text-white font-poster text-xl tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                        >
                            <Download className="w-5 h-5" aria-hidden="true" /> BAIXAR ARQUIVO
                        </a>
                        <button
                            onClick={reset}
                            data-testid="restart-button"
                            className="px-8 py-4 border-2 border-black bg-white hover:bg-black hover:text-white text-black font-poster text-xl tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                        >
                            <RotateCcw className="w-5 h-5" aria-hidden="true" /> NOVA OPERAÇÃO
                        </button>
                        {isPro && (
                            <button
                                onClick={saveHistory}
                                disabled={historySaved || historySaving}
                                data-testid="save-history-button"
                                className="px-8 py-4 border-2 border-black bg-black text-[#22D3EE] disabled:opacity-60 font-poster text-xl tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                            >
                                {historySaving ? (
                                    <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                                ) : (
                                    <Save className="w-5 h-5" aria-hidden="true" />
                                )}
                                {historySaved ? "SALVO NO HISTÓRICO" : historySaving ? "SALVANDO…" : "SALVAR NO HISTÓRICO"}
                            </button>
                        )}
                    </div>
                    {historyError && (
                        <p className="mt-3 text-xs text-red-700 font-medium" role="alert">{historyError}</p>
                    )}
                    {historySaved && (
                        <p className="mt-3 font-mono-tag text-[11px] text-gray-600" data-testid="history-saved-note">
                            Itens do histórico não são excluídos automaticamente.{" "}
                            <a href="/historico" className="underline">Ver histórico</a>
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
